import type { BillingTopupOptionsResponse } from "@server/types/responseTypes";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { createBillingPurchase } from "@/api/billing";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { queryKeys } from "@/lib/query-keys";
import { useOrganizationStore } from "@/store/organization";
import {
  type BillingAttempt,
  type BillingPurchaseSnapshot,
  readAttempt,
  reconcileAttempt,
  requiresPurchaseReview,
  saveAttempt,
} from "./billing-attempt";
import { formatCreditAmount } from "./billing-format";

/** Hosts one persisted org-bound attempt. Replay remains available after sales/catalog rollback. */
export function CreditTopUpDialog({
  orgId,
  options,
  attempt,
  onAttempt,
  onClose,
}: {
  orgId: string;
  options?: BillingTopupOptionsResponse["data"];
  attempt: BillingAttempt | null;
  onAttempt: (
    attempt: BillingAttempt,
    result?: BillingPurchaseSnapshot,
  ) => void;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const [offerId, setOfferId] = useState(attempt?.offerId ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [offerUnavailable, setOfferUnavailable] = useState(false);
  const inFlight = useRef(false);
  const checkout = useMutation({
    mutationKey: queryKeys.billing.checkout(orgId),
    mutationFn: async (identity: BillingAttempt) => {
      if (identity.purchaseId) {
        await client.cancelQueries({
          queryKey: queryKeys.billing.purchase(orgId, identity.purchaseId),
          exact: true,
        });
      }
      const result = await createBillingPurchase(
        orgId,
        identity.offerId,
        identity.formKey,
      );
      const queryKey = queryKeys.billing.purchase(orgId, result.purchase_id);
      // Reads started before replay cannot supersede its lifecycle result or clear its reference.
      await client.cancelQueries({ queryKey, exact: true });
      if (readAttempt(orgId)?.formKey !== identity.formKey) return null;
      const updated = { ...identity, purchaseId: result.purchase_id };
      saveAttempt(orgId, updated);
      reconcileAttempt(orgId, result);
      // Update origin ownership even after switching organizations; never cache Checkout URLs.
      client.setQueryData<BillingPurchaseSnapshot>(queryKey, (cached) => ({
        ...cached,
        purchase_id: result.purchase_id,
        payment_status: result.payment_status,
        fulfillment_status: result.fulfillment_status,
        review_required: result.review_required,
        review_message: null,
      }));
      return { result, updated };
    },
  });
  const submit = async () => {
    const active = useOrganizationStore.getState();
    if (
      inFlight.current ||
      active.organization?.id !== orgId ||
      active.organization.role !== "admin" ||
      active.isSwitching
    )
      return;
    inFlight.current = true;
    setBusy(true);
    setError(false);
    setOfferUnavailable(false);
    try {
      const saved = readAttempt(orgId);
      if (
        !saved &&
        (!options?.can_top_up ||
          !options.offers.some((offer) => offer.id === offerId))
      ) {
        setOfferId("");
        setOfferUnavailable(true);
        return;
      }
      const identity = saved ?? { formKey: crypto.randomUUID(), offerId };
      saveAttempt(orgId, identity);
      onAttempt(identity);
      const completed = await checkout.mutateAsync(identity);
      if (!completed) return;
      const { result, updated } = completed;
      const now = useOrganizationStore.getState();
      if (
        now.organization?.id !== orgId ||
        now.organization.role !== "admin" ||
        now.isSwitching
      )
        return;
      onAttempt(updated, result);
      void client.invalidateQueries({
        queryKey: queryKeys.billing.purchase(orgId, result.purchase_id),
        exact: true,
      });
      if (
        result.checkout_url &&
        result.payment_status !== "paid" &&
        !requiresPurchaseReview(result) &&
        result.fulfillment_status !== "fulfilled"
      ) {
        const url = new URL(result.checkout_url);
        if (
          url.protocol !== "https:" ||
          url.hostname !== "checkout.stripe.com" ||
          url.username ||
          url.password ||
          url.port
        )
          throw new Error("Invalid Checkout destination");
        window.location.assign(result.checkout_url);
      } else onClose();
    } catch {
      setError(true);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) onClose();
      }}
    >
      <DialogContent>
        <DialogTitle>
          {attempt ? "Resume your Checkout" : "Add credits"}
        </DialogTitle>
        <DialogDescription>
          {attempt
            ? "Continue the saved purchase using the same payment reference."
            : "Choose a one-time credit package. Stripe securely saves your card for future billing. This purchase does not change your auto-refill settings."}
        </DialogDescription>
        {attempt ? (
          <p>Saved package: {attempt.offerId}</p>
        ) : (
          <fieldset className="space-y-3">
            <legend className="sr-only">Credit package</legend>
            {options?.offers.map((offer) => (
              <label
                key={offer.id}
                className="flex items-center gap-3 rounded-md border p-4 cursor-pointer"
              >
                <input
                  type="radio"
                  name="offer"
                  value={offer.id}
                  aria-label={`${offer.label} — ${formatCreditAmount(offer.amount_minor, offer.currency)}`}
                  checked={offerId === offer.id}
                  onChange={() => {
                    setOfferId(offer.id);
                    setOfferUnavailable(false);
                  }}
                  disabled={busy}
                />
                <span>
                  {formatCreditAmount(offer.amount_minor, offer.currency)}
                </span>
              </label>
            ))}
          </fieldset>
        )}
        {offerUnavailable && (
          <p role="alert">
            This credit package is no longer available. Choose another package.
          </p>
        )}
        {error && (
          <p role="alert">
            Could not open Checkout. Try again with this saved purchase. If the
            problem continues, contact support; do not create another purchase
            for the same payment.
          </p>
        )}
        <Button
          disabled={busy || (!attempt && (!offerId || !options?.can_top_up))}
          onClick={() => void submit()}
        >
          {busy ? "Opening Checkout…" : "Continue to Checkout"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
